import { isRecord } from './normalize.ts';
import { indexWorkflow, type Scope, type StepLocation, type WorkflowIndex } from './scopes.ts';
import { getStepType } from './step-types.ts';
import { answerKeys } from './targets.ts';
import type { Family, Step, Workflow } from './types.ts';

export const END_NODE_ID = '__end';

export type GraphNodeKind = 'section' | 'step' | 'repeat' | 'end';

export interface GraphNode {
  id: string;
  kind: GraphNodeKind;
  parentId: string | null;
  title: string;
  number: number | null;
  stepType: string | null;
  family: Family | null;
}

export type EdgeKind = 'sequence' | 'answer' | 'condition' | 'otherwise';

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  kind: EdgeKind;
  label: string | null;
}

export interface WorkflowGraph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

interface LabelledTarget {
  target: string;
  kind: EdgeKind;
  label: string | null;
}

export function sectionNodeId(sectionId: string): string {
  return `section:${sectionId}`;
}

export function stepTitle(step: Step): string {
  if (typeof step.title === 'string' && step.title.trim()) return step.title;
  if (typeof step['say'] === 'string' && step['say'].trim()) return truncate(step['say']);
  if (typeof step['caption'] === 'string' && step['caption'].trim()) return truncate(step['caption']);
  return getStepType(step.type)?.title ?? step.type;
}

function truncate(text: string): string {
  return text.length > 60 ? `${text.slice(0, 57)}...` : text;
}

function answerLabel(step: Step, key: string): string {
  if (key === 'yes') return 'Yes';
  if (key === 'no') return 'No';
  const options = Array.isArray(step['options']) ? step['options'].filter(isRecord) : [];
  const option = options.find((o) => String(o['value']) === key);
  return option ? String(option['label']) : key;
}

function labelledTargets(step: Step): LabelledTarget[] {
  const next = step.next;
  if (next === undefined) return [{ target: 'next', kind: 'sequence', label: null }];
  if (typeof next === 'string') return [{ target: next, kind: 'sequence', label: null }];
  if (Array.isArray(next)) {
    const out: LabelledTarget[] = [];
    let hasOtherwise = false;
    for (const branch of next) {
      if (!isRecord(branch)) continue;
      if (typeof branch['when'] === 'string' && typeof branch['goto'] === 'string') {
        out.push({ target: branch['goto'], kind: 'condition', label: branch['when'] });
      } else if (typeof branch['otherwise'] === 'string') {
        out.push({ target: branch['otherwise'], kind: 'otherwise', label: 'otherwise' });
        hasOtherwise = true;
      }
    }
    if (!hasOtherwise) out.push({ target: 'next', kind: 'otherwise', label: 'otherwise' });
    return out;
  }
  if (isRecord(next)) {
    const out: LabelledTarget[] = Object.entries(next)
      .filter((e): e is [string, string] => typeof e[1] === 'string')
      .map(([key, target]) => ({ target, kind: 'answer' as const, label: answerLabel(step, key) }));
    const missing = answerKeys(step).filter((k) => !(k in next));
    if (missing.length > 0)
      out.push({ target: 'next', kind: 'answer', label: missing.map((k) => answerLabel(step, k)).join(', ') });
    return out;
  }
  return [{ target: 'next', kind: 'sequence', label: null }];
}

function resolve(location: StepLocation, target: string, index: WorkflowIndex): string[] {
  const { scope } = location;
  switch (target) {
    case 'end':
      return [END_NODE_ID];
    case 'next':
      if (location.index + 1 < scope.locations.length) return [scope.locations[location.index + 1].step.id];
      return scope.repeat ? [] : [END_NODE_ID];
    case 'continue':
      return [];
    case 'break':
      return afterRepeat(scope, index);
    default:
      return index.byId.has(target) ? [target] : [];
  }
}

function afterRepeat(scope: Scope, index: WorkflowIndex): string[] {
  if (!scope.repeat || !scope.parent) return [];
  const repeatLocation = scope.parent.locations.find((l) => l.step === scope.repeat);
  if (!repeatLocation) return [];
  return labelledTargets(scope.repeat).flatMap((t) => resolve(repeatLocation, t.target, index));
}

export function buildGraph(workflow: Workflow): WorkflowGraph {
  const index = indexWorkflow(workflow);
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const seenEdges = new Set<string>();
  const locationsBySection = new Map<string, StepLocation[]>();
  for (const location of index.all) {
    const list = locationsBySection.get(location.sectionId) ?? [];
    list.push(location);
    locationsBySection.set(location.sectionId, list);
  }
  for (const section of workflow.sections) {
    nodes.push({
      id: sectionNodeId(section.id),
      kind: 'section',
      parentId: null,
      title: section.title,
      number: null,
      stepType: null,
      family: null,
    });
    for (const location of locationsBySection.get(section.id) ?? []) {
      const { step } = location;
      nodes.push({
        id: step.id,
        kind: step.type === 'flow.repeat' ? 'repeat' : 'step',
        parentId: location.scope.repeat ? location.scope.repeat.id : sectionNodeId(section.id),
        title: stepTitle(step),
        number: location.number,
        stepType: step.type,
        family: getStepType(step.type)?.family ?? null,
      });
    }
  }
  nodes.push({
    id: END_NODE_ID,
    kind: 'end',
    parentId: null,
    title: 'End',
    number: null,
    stepType: null,
    family: null,
  });
  for (const location of index.all) {
    const { step } = location;
    const targets =
      step.type === 'flow.end' ? [{ target: 'end', kind: 'sequence' as const, label: null }] : labelledTargets(step);
    for (const t of targets) {
      for (const destination of resolve(location, t.target, index)) {
        const key = `${step.id}|${destination}|${t.label ?? ''}`;
        if (seenEdges.has(key)) continue;
        seenEdges.add(key);
        edges.push({
          id: `${step.id}->${destination}:${edges.length}`,
          source: step.id,
          target: destination,
          kind: t.kind,
          label: t.label,
        });
      }
    }
  }
  return { nodes, edges };
}
